// ═══════════════════════════════════════════════════════════
// ProfileModal — профіль користувача
// Зміна імені та пароля, валідація поточного пароля
// Модальне вікно з формою редагування облікових даних
// ═══════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAppData } from '../contexts/DataContext';
import { useT } from '../contexts/I18nContext';
import { useToast } from '../contexts/ToastContext';

const HOME_PAGE_OPTIONS = [
  { id: 'business_monitor', perm: 'business_monitor', labelKey: 'profile.homeBizMonitor' },
  { id: 'dashboard', perm: null, labelKey: 'profile.homeDashboard' },
  { id: 'pos', perm: 'pos_sell', labelKey: 'profile.homePos' },
  { id: 'orders', perm: 'orders_view', labelKey: 'profile.homeOrders' },
  { id: 'calendar', perm: 'calendar_view', labelKey: 'profile.homeCalendar' },
  { id: 'clients', perm: 'clients_view', labelKey: 'profile.homeClients' },
  { id: 'communication', perm: 'communication_view', labelKey: 'profile.homeCommunication' },
  { id: 'staff', perm: 'staff_view', labelKey: 'profile.homeStaff' },
  { id: 'inventory_stock', perm: 'inventory_view', labelKey: 'profile.homeStock' },
  { id: 'inventory_purchases', perm: 'inventory_purchases', labelKey: 'profile.homePurchases' },
  { id: 'production', perm: 'production_view', labelKey: 'profile.homeProduction' },
  { id: 'salary_calc', perm: 'salary_view', labelKey: 'profile.homeSalary' },
  { id: 'finances', perm: 'finances_view', labelKey: 'profile.homeFinances' },
  { id: 'cash_register', perm: 'cash_register', labelKey: 'profile.homeCashRegister' },
  { id: 'reports', perm: 'reports_view', labelKey: 'profile.homeReports' },
  { id: 'financial_result', perm: 'financial_result', labelKey: 'profile.homeFinResult' },
];

export default function ProfileModal({ onClose, hasPermission }) {
  const { userProfile, changePassword, updateDisplayName, updateHomePage, updateStartingLocation } = useAuth();
  const { locations } = useAppData();
  const toast = useToast();
  const { t } = useT();

  const [displayName, setDisplayName] = useState(userProfile?.displayName || '');
  const [homePage, setHomePage] = useState(userProfile?.homePage || 'dashboard');
  // '' means "no preference" — fall back to locs[0] behavior on load.
  const [startingLocationId, setStartingLocationId] = useState(
    userProfile?.startingLocationId ? String(userProfile.startingLocationId) : ''
  );
  const availablePages = HOME_PAGE_OPTIONS.filter(p => !p.perm || !hasPermission || hasPermission(p.perm));
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (!displayName.trim()) {
      setError(t('profile.errorEnterName'));
      return;
    }

    setBusy(true);
    try {
      if (displayName.trim() !== userProfile?.displayName) {
        await updateDisplayName(displayName.trim());
      }
      if (homePage !== (userProfile?.homePage || 'dashboard')) {
        await updateHomePage(homePage);
      }
      const currentStarting = userProfile?.startingLocationId ? String(userProfile.startingLocationId) : '';
      if (startingLocationId !== currentStarting) {
        await updateStartingLocation(startingLocationId || null);
      }

      if (currentPassword || newPassword || confirmPassword) {
        if (!currentPassword) {
          setError(t('profile.errorEnterCurrentPassword'));
          setBusy(false);
          return;
        }
        if (newPassword.length < 6) {
          setError(t('profile.errorMinPassword'));
          setBusy(false);
          return;
        }
        if (newPassword !== confirmPassword) {
          setError(t('profile.errorPasswordMismatch'));
          setBusy(false);
          return;
        }
        await changePassword(currentPassword, newPassword);
      }

      setSuccess(t('profile.saved'));
      toast.success(currentPassword ? t('profile.passwordChanged') : t('profile.profileUpdated'));
      setTimeout(() => onClose(), 1000);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError(t('profile.errorWrongPassword'));
      } else if (err.code === 'auth/too-many-requests') {
        setError(t('profile.errorTooMany'));
      } else {
        setError(t('profile.errorGeneric') + err.message);
      }
    }
    setBusy(false);
  };

  const PasswordToggle = ({ show, onToggle }) => (
    <button type="button" className="password-toggle-btn" onClick={onToggle} tabIndex={-1}
      title={show ? t('login.hidePassword') : t('login.showPassword')}>
      {show ? (
        <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
          <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
          <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
        </svg>
      ) : (
        <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
        </svg>
      )}
    </button>
  );

  return (
    <div className="modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal profile-modal">
        <div className="modal-header">
          <h2>{t('profile.title')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-field">
            <label className="field-label">{t('profile.name')}</label>
            <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
              placeholder={t('profile.namePlaceholder')} />
          </div>

          <div className="form-field">
            <label className="field-label">{t('profile.email')}</label>
            <input type="email" value={userProfile.email} disabled className="profile-disabled-input" />
          </div>

          <div className="form-field">
            <label className="field-label">{t('profile.homePage')}</label>
            <select value={homePage} onChange={e => setHomePage(e.target.value)}>
              {availablePages.map(p => (
                <option key={p.id} value={p.id}>{t(p.labelKey)}</option>
              ))}
            </select>
          </div>

          {(locations || []).length > 1 && (
            <div className="form-field">
              <label className="field-label">{t('profile.startingLocation')}</label>
              <select
                value={startingLocationId}
                onChange={e => setStartingLocationId(e.target.value)}
              >
                <option value="">{t('profile.startingLocationAuto')}</option>
                {(locations || []).map(l => (
                  <option key={l.id} value={String(l.id)}>{l.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="profile-password-section">
            <h3>{t('profile.changePassword')}</h3>
            <small style={{ color: 'var(--text-muted)', display: 'block', marginBottom: '0.75rem' }}>
              {t('profile.passwordHint')}
            </small>

            <div className="form-field">
              <label className="field-label">{t('profile.currentPassword')}</label>
              <div className="password-input-wrap">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  placeholder="••••••"
                  autoComplete="current-password"
                />
                <PasswordToggle show={showCurrentPassword} onToggle={() => setShowCurrentPassword(p => !p)} />
              </div>
            </div>

            <div className="form-field">
              <label className="field-label">{t('profile.newPassword')}</label>
              <div className="password-input-wrap">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder={t('profile.newPasswordPlaceholder')}
                  autoComplete="new-password"
                />
                <PasswordToggle show={showNewPassword} onToggle={() => setShowNewPassword(p => !p)} />
              </div>
            </div>

            <div className="form-field">
              <label className="field-label">{t('profile.confirmPassword')}</label>
              <div className="password-input-wrap">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder={t('profile.confirmPasswordPlaceholder')}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>

          {error && <div className="login-error">{error}</div>}
          {success && <div className="profile-success">{success}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={busy || !displayName.trim()}>
            {busy ? t('login.wait') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
